import React, { useMemo, useState } from 'react';
import * as wasm from 'sched';
import { FiTrash2, FiMove } from 'react-icons/fi';

export interface AvailabilityListItem {
  id: string;
  policy: wasm.AvailabilityPolicy;
  after?: string | null;
  before?: string | null;
}

interface AvailabilityListProps {
  items: AvailabilityListItem[];
  onDelete: (id: string) => Promise<void>;
  onReorder: (opts: { id: string; before: string | null }) => Promise<void>;
  emptyText?: string;
}

function formatMatcher(matcher: wasm.DayMatcher): string {
  switch (matcher.type) {
    case 'MonTueWedThuFri':
      return 'Mon\u2013Fri';
    case 'SatSun':
      return 'Sat\u2013Sun';
    case 'EveryDay':
      return 'Every day';
    case 'USHoliday':
      return 'US Holidays';
    case 'UKHoliday':
      return 'UK Holidays';
    case 'SpecifiedDays':
      return `${matcher.days.length} date(s)`;
    default:
      return 'Policy';
  }
}

function formatCapacity(tenths: number): string {
  if (tenths === 0) return 'off';
  if (tenths === 10) return '100%';
  return `${tenths * 10}%`;
}

export const AvailabilityList: React.FC<AvailabilityListProps> = ({ items, onDelete, onReorder, emptyText = 'No availability policies yet.' }) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const list = useMemo(() => items, [items]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnItem = async (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    const dragged = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    if (!dragged || dragged === targetId) return;
    await onReorder({ id: dragged, before: targetId });
  };

  const handleDropAtEnd = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dragged = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    if (!dragged) return;
    await onReorder({ id: dragged, before: null });
  };

  if (!list || list.length === 0) {
    return <div className="text-sm text-cm-text-muted py-2">{emptyText}</div>;
  }

  return (
    <div className="space-y-1.5">
      {list.map((av, idx) => {
        const cap = (av.policy as any)?.capacity ?? 10;
        const matcher = (av.policy as any)?.matcher ?? av.policy;
        return (
          <div
            key={av.id}
            draggable
            onDragStart={(e) => handleDragStart(e, av.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDropOnItem(e, av.id)}
            className={`flex items-center justify-between rounded-lg border border-cm-border-subtle px-3 py-2 transition-opacity ${draggingId === av.id ? 'opacity-40' : 'hover:border-cm-border-mid'}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <FiMove className="w-3.5 h-3.5 text-cm-text-muted shrink-0 cursor-grab" />
              <span className="text-xs text-cm-text-muted font-mono w-4 shrink-0">{idx + 1}.</span>
              <span className="text-sm font-medium text-cm-text-primary">{formatMatcher(matcher)}</span>
              <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${cap === 0 ? 'bg-red-500/20 text-red-400' : cap === 10 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {formatCapacity(cap)}
              </span>
              {(av.after || av.before) && (
                <span className="text-[10px] text-cm-text-muted">
                  {av.after && av.before ? `${av.after} \u2013 ${av.before}` : av.after ? `from ${av.after}` : `until ${av.before}`}
                </span>
              )}
              {matcher.type === 'SpecifiedDays' && (
                <span className="text-[10px] text-cm-text-muted truncate max-w-[200px]">
                  ({(matcher.days || []).join(', ')})
                </span>
              )}
            </div>
            <button
              onClick={() => onDelete(av.id)}
              className="p-1 rounded text-cm-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
              aria-label="Delete policy"
            >
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDropAtEnd}
        className="border border-dashed border-cm-border-subtle rounded-lg py-2 text-center text-[10px] text-cm-text-muted"
      >
        Drop here to move to end
      </div>
    </div>
  );
};

export default AvailabilityList;
